/**
 * Geometry data generation for thick lines. Intended to be used with a specific stroke shader that offsets the vertices based on specific attributes.
 * @module
 */
import type { PolyLine2 } from "geom/polyLine2Types";

const EPSILON = 1e-6;

export enum CapType {
  Butt, // add nothing on the cap
  Square, // make square centered on start pt
  Round, // round on end
  TriArrow, // triangular arrow end
}

export enum JoinType {
  Round,
  Miter,
  Bevel,
}

const JoinTypeRound = JoinType.Round;
const JoinTypeMiter = JoinType.Miter;
const JoinTypeBevel = JoinType.Bevel;
const { abs, sqrt, atan2, PI, cos, sin, max } = Math;
const TWO_PI = PI * 2;

export interface StrokeStyle {
  joinType: JoinType;
  miterLimit: number;
  roundAngleIncrement: number;

  capTypeStart: CapType;
  capTypeEnd: CapType;

  startArrowLength: number;
  startArrowWidth: number;

  endArrowLength: number;
  endArrowWidth: number;

  allowDashed: boolean; // Internally this will create separate vertices for different segments so that distance along the line can be determined at each pixel.
}

export const DefaultStrokeStyle: StrokeStyle = {
  capTypeStart: CapType.Butt,
  startArrowLength: 0.4,
  startArrowWidth: 0.1,

  capTypeEnd: CapType.Butt,
  endArrowLength: 3,
  endArrowWidth: 1,

  joinType: JoinType.Miter,
  miterLimit: 10,
  roundAngleIncrement: 0.25, // ~12 increments for 180 degrees

  allowDashed: true,
};

export class StrokedGeom {
  pts: number[];
  norms: number[];
  tris: number[];
  tex: number[]; // the texture normal
  dst: number[]; // distance along the line

  clear() {
    const { pts, norms, tris, tex, dst } = this;
    // this is fast as it doesn't cause realloc
    while (pts.length > 0) {
      pts.pop();
    }
    while (norms.length > 0) {
      norms.pop();
    }
    while (tris.length > 0) {
      tris.pop();
    }
    while (tex.length > 0) {
      tex.pop();
    }
    while (dst.length > 0) {
      dst.pop();
    }
  }

  constructor() {
    this.pts = [];
    this.norms = [];
    this.tris = [];
    this.tex = [];
    this.dst = [];
  }
}

export function strokePolyLine2(
  pts: PolyLine2,
  style: StrokeStyle,
  startIndex: number = 0,
  closed: boolean = false,
): StrokedGeom {
  const res = new StrokedGeom();
  strokePolyline2Mutate(res, pts, style, startIndex, closed);
  return res;
}

export function strokePolyline2Mutate(
  res: StrokedGeom,
  pts: PolyLine2,
  style: StrokeStyle,
  startIndex: number = 0,
  closed: boolean = false,
): StrokedGeom {
  const numPoints = pts.length >> 1;
  if (numPoints < 2) {
    return res;
  }

  const { miterLimit, capTypeStart, capTypeEnd, roundAngleIncrement, allowDashed } = style;
  const { joinType } = style;

  let count = numPoints;
  // Start from a non-zero value so that the shader can always use sign(distanceValue).
  // The value 2.0 is somewhat arbitrary but a value that's enough above zero helps avoid
  // small clipped sections of a dash at the line start.
  let dstValue = 2.0;
  if (closed) {
    init(pts, joinType, miterLimit, res, dstValue);
  } else {
    count--;
    cap(true, pts, startIndex, dstValue, capTypeStart, roundAngleIncrement, res, style);
  }

  let madeJoin = false;
  for (let i = 1; i < count; i++) {
    const distanceX = pts[i * 2] - pts[(i - 1) * 2];
    const distanceY = pts[i * 2 + 1] - pts[(i - 1) * 2 + 1];
    dstValue += Math.sqrt(distanceX * distanceX + distanceY * distanceY);
    madeJoin =
      join(i, pts, startIndex, dstValue, joinType, miterLimit, roundAngleIncrement, allowDashed, res) || madeJoin;
  }

  if (!madeJoin && numPoints !== 2) {
    return new StrokedGeom();
  }

  if (!closed) {
    const distanceX = pts[pts.length - 2] - pts[pts.length - 4];
    const distanceY = pts[pts.length - 1] - pts[pts.length - 3];
    dstValue += Math.sqrt(distanceX * distanceX + distanceY * distanceY);
    cap(false, pts, startIndex, dstValue, capTypeEnd, roundAngleIncrement, res, style);
  }

  return res;
}

function findPrevNotCoincidentPoint(pts: PolyLine2, b: number): number[] | null {
  const bx = pts[b * 2];
  const by = pts[b * 2 + 1];
  for (let i = 1; i < pts.length / 2; i++) {
    const a = (pts.length / 2 + b - i) % (pts.length / 2);
    const ax = pts[a * 2];
    const ay = pts[a * 2 + 1];
    if (bx !== ax || by !== ay) {
      return [ax, ay];
    }
  }
  return null;
}

function findNextNotCoincidentPoint(pts: PolyLine2, b: number): number[] | null {
  const bx = pts[b * 2];
  const by = pts[b * 2 + 1];
  for (let i = 1; i < pts.length / 2; i++) {
    const c = (b + i) % (pts.length / 2);
    const cx = pts[c * 2];
    const cy = pts[c * 2 + 1];
    if (bx !== cx || by !== cy) {
      return [cx, cy];
    }
  }
  return null;
}

// corner draws a corner given the index of the pt and inserts it into strokedGeom
function join(
  b: number,
  tess: PolyLine2,
  startIndex: number,
  dstValue: number,
  jtype: JoinType,
  miterLimit: number,
  roundAngleIncrement: number,
  allowDashed: boolean,
  res: StrokedGeom,
): boolean {
  const pts = res.pts;
  const norms = res.norms;
  const tris = res.tris;
  const tex = res.tex;
  const dst = res.dst;

  const a = findPrevNotCoincidentPoint(tess, b);
  if (!a) {
    return false;
  }

  const c = findNextNotCoincidentPoint(tess, b);
  if (!c) {
    return false;
  }

  const ax = a[0];
  const ay = a[1];

  const bx = tess[b * 2];
  const by = tess[b * 2 + 1];

  const cx = c[0];
  const cy = c[1];

  let abx = bx - ax;
  let aby = by - ay;

  let bcx = cx - bx;
  let bcy = cy - by;

  let abl = sqrt(abx * abx + aby * aby);
  let bcl = sqrt(bcx * bcx + bcy * bcy);

  const perpdot = -aby * bcx + abx * bcy;
  const turningRight = perpdot < 0;
  if (abs(perpdot) < EPSILON && abl < EPSILON && bcl < EPSILON) {
    return false;
  }

  if (abl < EPSILON) {
    abx = bcx;
    aby = bcy;
    abl = bcl;
  }

  if (bcl < EPSILON) {
    bcx = abx;
    bcy = aby;
    bcl = abl;
  }

  const iabl = 1 / abl;
  const ibcl = 1 / bcl;
  const abnx = -aby * iabl;
  const abny = abx * iabl;

  const bcnx = -bcy * ibcl;
  const bcny = bcx * ibcl;

  // we'll need this for indexing into the triangles
  let oi = startIndex + res.pts.length / 2 - 2;

  // the sum of the two norms at b
  let fnormx = abnx + bcnx;
  let fnormy = abny + bcny;

  // the length of the extension.
  // abs() is needed to cover a corner case where the divisor ends up as a very small negative number in case the line does a u-turn. Normally the divisor is positive.
  const len = abs(1 / (fnormx * abnx + fnormy * abny));

  fnormx = fnormx * len;
  fnormy = fnormy * len;

  // Sign of dstValue is used to mark whether the distance value should be offset forward or backward in the shader.
  const turningRightSign = turningRight ? 1.0 : -1.0;

  switch (jtype) {
    case JoinTypeMiter:
      pts.push(bx);
      pts.push(by);
      tex.push(2);
      dst.push(turningRightSign * dstValue);

      pts.push(bx);
      pts.push(by);
      tex.push(-2);
      dst.push(-turningRightSign * dstValue);

      tris.push(oi);
      tris.push(oi + 1);
      tris.push(oi + 2);

      tris.push(oi + 1);
      tris.push(oi + 3);
      tris.push(oi + 2);

      if (len > miterLimit) {
        pts.push(bx);
        pts.push(by);
        tex.push(2);
        dst.push(-turningRightSign * dstValue);

        pts.push(bx);
        pts.push(by);
        tex.push(-2);
        dst.push(turningRightSign * dstValue);

        norms.push(abnx);
        norms.push(abny);
        norms.push(-abnx);
        norms.push(-abny);

        norms.push(bcnx);
        norms.push(bcny);
        norms.push(-bcnx);
        norms.push(-bcny);
      } else if (allowDashed) {
        norms.push(abnx);
        norms.push(abny);
        norms.push(-abnx);
        norms.push(-abny);

        // Fill in the section between the line segments with separate geometry.
        // We do this so that we can always draw this section solid colored,
        // which hides some artifacts.
        pts.push(bx);
        pts.push(by);
        pts.push(bx);
        pts.push(by);

        tex.push(2);
        tex.push(-2);
        dst.push(dstValue);
        dst.push(dstValue);

        norms.push(fnormx);
        norms.push(fnormy);
        norms.push(-fnormx);
        norms.push(-fnormy);

        tris.push(turningRight ? oi + 2 : oi + 3); // Reuse a vertex from the end of the previous line segment.
        tris.push(oi + 4);
        tris.push(oi + 5);

        tris.push(oi + 4);
        tris.push(turningRight ? oi + 6 : oi + 7); // Reuse a vertex from the start of the next line segment.
        tris.push(oi + 5);

        // Start of the next line segment
        pts.push(bx);
        pts.push(by);
        tex.push(1);
        dst.push(-turningRightSign * dstValue);

        pts.push(bx);
        pts.push(by);
        tex.push(-1);
        dst.push(turningRightSign * dstValue);

        norms.push(bcnx);
        norms.push(bcny);
        norms.push(-bcnx);
        norms.push(-bcny);
      } else {
        norms.push(fnormx);
        norms.push(fnormy);
        norms.push(-fnormx);
        norms.push(-fnormy);
      }

      return true;
    case JoinTypeRound:
    case JoinTypeBevel:
      pts.push(bx);
      pts.push(by);

      pts.push(bx);
      pts.push(by);

      if (len > miterLimit) {
        // end butt cap
        norms.push(abnx);
        norms.push(abny);
        norms.push(-abnx);
        norms.push(-abny);

        tex.push(1);
        tex.push(-1);
        dst.push(turningRightSign * dstValue);
        dst.push(-turningRightSign * dstValue);

        tris.push(oi);
        tris.push(oi + 1);
        tris.push(oi + 2);
        tris.push(oi + 1);
        tris.push(oi + 3);
        tris.push(oi + 2);

        if (jtype === JoinTypeRound) {
          // we'll start the butt cap after inserting the round
          break;
        }

        // start butt cap
        pts.push(bx);
        pts.push(by);
        tex.push(1);
        dst.push(-turningRightSign * dstValue);

        pts.push(bx);
        pts.push(by);
        tex.push(-1);
        dst.push(turningRightSign * dstValue);

        norms.push(bcnx);
        norms.push(bcny);
        norms.push(-bcnx);
        norms.push(-bcny);
        return true;
      }

      pts.push(bx);
      pts.push(by);

      if (turningRight) {
        tex.push(1);
        tex.push(1);
        tex.push(-1);
        dst.push(turningRightSign * dstValue);
        dst.push(turningRightSign * dstValue);
        dst.push(-turningRightSign * dstValue);

        norms.push(abnx);
        norms.push(abny);
        norms.push(bcnx);
        norms.push(bcny);
        norms.push(-fnormx);
        norms.push(-fnormy);

        tris.push(oi);
        tris.push(oi + 1);
        tris.push(oi + 4);
        tris.push(oi);
        tris.push(oi + 4);
        tris.push(oi + 2);

        if (!allowDashed && jtype !== JoinTypeRound) {
          // Fill the section between line segments with a single triangle.
          // If the line is dashed we don't do this, since we'll disable distance offset in this section.
          tris.push(oi + 2);
          tris.push(oi + 4);
          tris.push(oi + 3);
        }

        if (allowDashed) {
          // Duplicate the vertices with inverted signs for the distance offset.
          // This way we can generate unique distance values for vertices on both sides of the join in the shader.
          pts.push(bx);
          pts.push(by);

          pts.push(bx);
          pts.push(by);

          tex.push(1);
          tex.push(-1);
          dst.push(-turningRightSign * dstValue);
          dst.push(turningRightSign * dstValue);

          norms.push(bcnx);
          norms.push(bcny);
          norms.push(-fnormx);
          norms.push(-fnormy);
        }
      } else {
        tex.push(-1);
        tex.push(1);
        tex.push(-1);
        dst.push(turningRightSign * dstValue);
        dst.push(turningRightSign * dstValue);
        dst.push(turningRightSign * dstValue);

        norms.push(-abnx);
        norms.push(-abny);
        norms.push(fnormx);
        norms.push(fnormy);
        norms.push(-bcnx);
        norms.push(-bcny);

        tris.push(oi);
        tris.push(oi + 1);
        tris.push(oi + 2);
        tris.push(oi);
        tris.push(oi + 2);
        tris.push(oi + 3);

        if (!allowDashed && jtype !== JoinTypeRound) {
          // Fill the section between line segments with a single triangle.
          // If the line is dashed we don't do this, since we'll disable distance offset in this section.
          tris.push(oi + 3);
          tris.push(oi + 2);
          tris.push(oi + 4);
        }

        if (allowDashed) {
          // Duplicate the vertices with inverted signs for the distance offset.
          // This way we can generate unique distance values for vertices on both sides of the join in the shader.
          pts.push(bx);
          pts.push(by);

          pts.push(bx);
          pts.push(by);

          tex.push(1);
          tex.push(-1);
          dst.push(-turningRightSign * dstValue);
          dst.push(turningRightSign * dstValue);

          norms.push(fnormx);
          norms.push(fnormy);
          norms.push(-bcnx);
          norms.push(-bcny);
        }
      }
  }

  if (jtype !== JoinTypeRound && !allowDashed) {
    return true;
  }

  // Fill in the section between the line segments.

  // don't reference any of the points before this
  oi = startIndex + res.pts.length / 2;

  // insert triangle fans
  let diffAng = 0;
  let x = 0;
  let y = 0;
  if (abs(perpdot) < EPSILON) {
    // this is a 180 degree turn, we lazily draw a circle
    diffAng = TWO_PI;
    x = abnx;
    y = abny;
  } else if (turningRight) {
    diffAng = atan2(-bcny, -bcnx) - atan2(-abny, -abnx);
    x = abnx;
    y = abny;
  } else {
    diffAng = atan2(bcny, bcnx) - atan2(abny, abnx);
    x = -abnx;
    y = -abny;
  }

  if (abs(diffAng) > PI) {
    diffAng += TWO_PI;
  }

  let numSegs = max(abs(diffAng / roundAngleIncrement) | 0, 1);

  if (jtype !== JoinTypeRound) {
    numSegs = 1;
  }

  const theta = diffAng / numSegs;
  const costheta = cos(theta);
  const sintheta = sin(theta);
  let t = 0;

  pts.push(bx);
  pts.push(by);
  tex.push(-turningRightSign * 2); // tex values > 1 signal to disable distance offset. This is done so that the join will be filled with a solid color, covering artifacts.
  dst.push(dstValue);

  pts.push(bx);
  pts.push(by);
  tex.push(turningRightSign * 2);
  dst.push(dstValue);

  // Inner point
  norms.push(-turningRightSign * fnormx);
  norms.push(-turningRightSign * fnormy);

  // Outer edge point
  norms.push(x);
  norms.push(y);

  for (let i = 0; i < numSegs; i++) {
    t = x;
    x = costheta * x - sintheta * y;
    y = sintheta * t + costheta * y;

    pts.push(bx);
    pts.push(by);
    tex.push(turningRightSign * 2);
    dst.push(dstValue);

    norms.push(x);
    norms.push(y);

    tris.push(oi);
    tris.push(oi + i + 1);
    tris.push(oi + i + 2);
  }

  pts.push(bx);
  pts.push(by);
  tex.push(1);
  dst.push(dstValue);

  pts.push(bx);
  pts.push(by);
  tex.push(-1);
  dst.push(dstValue);

  if (len > miterLimit) {
    // start butt cap
    norms.push(bcnx);
    norms.push(bcny);
    norms.push(-bcnx);
    norms.push(-bcny);
    return true;
  }

  if (turningRight) {
    norms.push(bcnx);
    norms.push(bcny);
    norms.push(-fnormx);
    norms.push(-fnormy);
    return true;
  }

  norms.push(fnormx);
  norms.push(fnormy);
  norms.push(-bcnx);
  norms.push(-bcny);

  return true;
}

function init(tess: PolyLine2, jtype: JoinType, miterLimit: number, res: StrokedGeom, dstValue: number) {
  const pts = res.pts;
  const norms = res.norms;
  const tex = res.tex;
  const dst = res.dst;

  const a = findPrevNotCoincidentPoint(tess, 0);
  if (!a) {
    return;
  }
  const c = findNextNotCoincidentPoint(tess, 0);
  if (!c) {
    return;
  }

  const ax = a[0];
  const ay = a[1];

  const bx = tess[0];
  const by = tess[1];

  const cx = c[0];
  const cy = c[1];

  const abx = bx - ax;
  const aby = by - ay;

  const bcx = cx - bx;
  const bcy = cy - by;

  const perpdot = -aby * bcx + abx * bcy;
  const turningRight = perpdot < 0;
  const turningRightSign = turningRight ? 1.0 : -1.0;

  const abl = sqrt(abx * abx + aby * aby);
  const bcl = sqrt(bcx * bcx + bcy * bcy);

  const iabl = 1 / abl;
  const ibcl = 1 / bcl;

  const abnx = -aby * iabl;
  const abny = abx * iabl;

  const bcnx = -bcy * ibcl;
  const bcny = bcx * ibcl;

  // the sum of the two norms at b
  let fnormx = abnx + bcnx;
  let fnormy = abny + bcny;

  // the length of the extension
  const len = 1 / (fnormx * abnx + fnormy * abny);

  // scale
  fnormx = fnormx * len;
  fnormy = fnormy * len;

  pts.push(bx);
  pts.push(by);
  tex.push(1);
  dst.push(-turningRightSign * dstValue);

  pts.push(bx);
  pts.push(by);
  tex.push(-1);
  dst.push(turningRightSign * dstValue);

  if (len > miterLimit) {
    if (bcl > EPSILON) {
      norms.push(bcnx);
      norms.push(bcny);
      norms.push(-bcnx);
      norms.push(-bcny);
    } else {
      // if bc is zero length
      norms.push(0);
      norms.push(0);
      norms.push(0);
      norms.push(0);
    }

    return;
  }

  switch (jtype) {
    case JoinTypeMiter:
      norms.push(fnormx);
      norms.push(fnormy);
      norms.push(-fnormx);
      norms.push(-fnormy);

      break;
    case JoinTypeRound:
    case JoinTypeBevel:
      if (turningRight) {
        norms.push(bcnx);
        norms.push(bcny);
        norms.push(-fnormx);
        norms.push(-fnormy);
        return;
      }

      norms.push(fnormx);
      norms.push(fnormy);
      norms.push(-bcnx);
      norms.push(-bcny);
  }
}

function cap(
  isStart: boolean,
  tess: PolyLine2,
  startIndex: number,
  dstValue: number,
  ctype: CapType,
  roundAngleIncrement: number,
  res: StrokedGeom,
  style: StrokeStyle,
) {
  const pts = res.pts;
  const norms = res.norms;
  const tris = res.tris;
  const tex = res.tex;
  const dst = res.dst;

  let a = 0;
  let b = 1;

  if (!isStart) {
    a = tess.length / 2 - 2;
    b = tess.length / 2 - 1;
  }

  let ax = tess[a * 2];
  let ay = tess[a * 2 + 1];

  const bx = tess[b * 2];
  const by = tess[b * 2 + 1];

  const abx = bx - ax;
  const aby = by - ay;

  // TODO what about if 0?
  const abl = sqrt(abx * abx + aby * aby);

  const abnx = -aby / abl;
  const abny = abx / abl;

  // we'll need this for indexing into the triangles
  let oi = startIndex + res.pts.length / 2 - 2;

  switch (ctype) {
    case CapType.Butt:
      if (isStart) {
        // two copies of original pts
        pts.push(ax);
        pts.push(ay);
        tex.push(1);
        dst.push(dstValue);

        pts.push(ax);
        pts.push(ay);
        tex.push(-1);
        dst.push(dstValue);
      } else {
        pts.push(bx);
        pts.push(by);
        tex.push(1);
        dst.push(dstValue);

        pts.push(bx);
        pts.push(by);
        tex.push(-1);
        dst.push(dstValue);
      }

      // the normals, one on the left, one on the right
      norms.push(abnx);
      norms.push(abny);
      norms.push(-abnx);
      norms.push(-abny);

      if (!isStart) {
        // add triangles
        tris.push(oi);
        tris.push(oi + 1);
        tris.push(oi + 2);

        tris.push(oi + 1);
        tris.push(oi + 3);
        tris.push(oi + 2);
      }

      break;
    case CapType.Square:
      // vector aligned with axis
      const abnnx = abx / abl;
      const abnny = aby / abl;

      if (isStart) {
        // two copies of original pts
        pts.push(ax);
        pts.push(ay);
        tex.push(1);
        dst.push(dstValue);

        pts.push(ax);
        pts.push(ay);
        tex.push(-1);
        dst.push(dstValue);

        norms.push(abnx - abnnx);
        norms.push(abny - abnny);

        norms.push(-abnx - abnnx);
        norms.push(-abny - abnny);
      } else {
        pts.push(bx);
        pts.push(by);
        tex.push(1);
        dst.push(dstValue);

        pts.push(bx);
        pts.push(by);
        tex.push(-1);
        dst.push(dstValue);

        norms.push(abnx + abnnx);
        norms.push(abny + abnny);

        norms.push(-abnx + abnnx);
        norms.push(-abny + abnny);

        tris.push(oi);
        tris.push(oi + 1);
        tris.push(oi + 2);

        tris.push(oi + 1);
        tris.push(oi + 3);
        tris.push(oi + 2);
      }
      break;
    case CapType.TriArrow:
      const vscale = isStart ? -style.startArrowLength : style.endArrowLength;
      const hscale = isStart ? style.startArrowWidth : style.endArrowWidth;

      let px;
      let py;

      if (isStart) {
        px = ax;
        py = ay;
      } else {
        px = bx;
        py = by;

        pts.push(bx - (vscale * abx) / abl);
        pts.push(by - (vscale * aby) / abl);
        tex.push(1);
        dst.push(dstValue);

        pts.push(bx - (vscale * abx) / abl);
        pts.push(by - (vscale * aby) / abl);
        tex.push(-1);
        dst.push(dstValue);

        // the normals, one on the left, one on the right
        norms.push(abnx);
        norms.push(abny);
        norms.push(-abnx);
        norms.push(-abny);

        // add triangles
        tris.push(oi);
        tris.push(oi + 1);
        tris.push(oi + 2);

        tris.push(oi + 1);
        tris.push(oi + 3);
        tris.push(oi + 2);
      }

      pts.push(px);
      pts.push(py);
      tex.push(1);
      dst.push(dstValue);

      pts.push(px - (vscale * abx) / abl + hscale * abnx);
      pts.push(py - (vscale * aby) / abl + hscale * abny);
      tex.push(1);
      dst.push(dstValue);

      pts.push(px - (vscale * abx) / abl - hscale * abnx);
      pts.push(py - (vscale * aby) / abl - hscale * abny);
      tex.push(-1);
      dst.push(dstValue);

      norms.push(0);
      norms.push(0);

      norms.push(0);
      norms.push(0);

      norms.push(0);
      norms.push(0);

      if (isStart) {
        tris.push(oi + 2);
        tris.push(oi + 3);
        tris.push(oi + 4);
      } else {
        // the recursive call adds 2 new pts
        tris.push(oi + 4);
        tris.push(oi + 5);
        tris.push(oi + 6);
        return;
      }

      if (isStart) {
        // two copies of original pts
        pts.push(ax - (vscale * abx) / abl);
        pts.push(ay - (vscale * aby) / abl);
        tex.push(1);
        dst.push(dstValue);

        pts.push(ax - (vscale * abx) / abl);
        pts.push(ay - (vscale * aby) / abl);
        tex.push(-1);
        dst.push(dstValue);

        // the normals, one on the left, one on the right
        norms.push(abnx);
        norms.push(abny);
        norms.push(-abnx);
        norms.push(-abny);
      }

      break;
    case CapType.Round:
      // make preceding leg
      if (!isStart) {
        pts.push(bx);
        pts.push(by);
        tex.push(1);
        dst.push(dstValue);

        pts.push(bx);
        pts.push(by);
        tex.push(-1);
        dst.push(dstValue);

        norms.push(abnx);
        norms.push(abny);
        norms.push(-abnx);
        norms.push(-abny);

        tris.push(oi);
        tris.push(oi + 1);
        tris.push(oi + 2);
        tris.push(oi + 1);
        tris.push(oi + 3);
        tris.push(oi + 2);
      }

      oi = startIndex + res.pts.length / 2;

      let diffAng = PI;
      if (isStart) {
        diffAng = -diffAng;
      }

      const numSegs = abs(diffAng / roundAngleIncrement) | 0;

      const theta = diffAng / numSegs;
      const costheta = cos(theta);
      const sintheta = sin(theta);
      let t = 0;

      let x = -abnx;
      let y = -abny;

      if (!isStart) {
        ax = bx;
        ay = by;
      }

      pts.push(ax);
      pts.push(ay);
      tex.push(0);
      dst.push(dstValue);

      pts.push(ax);
      pts.push(ay);
      tex.push(1);
      dst.push(dstValue);

      norms.push(0);
      norms.push(0);
      norms.push(x);
      norms.push(y);

      for (let i = 0; i < numSegs; i++) {
        t = x;
        x = costheta * x - sintheta * y;
        y = sintheta * t + costheta * y;

        pts.push(ax);
        pts.push(ay);
        tex.push(1);
        dst.push(dstValue);

        norms.push(x);
        norms.push(y);

        tris.push(oi);
        tris.push(oi + i + 1);
        tris.push(oi + i + 2);
      }

      if (isStart) {
        pts.push(ax);
        pts.push(ay);
        tex.push(1);
        dst.push(dstValue);

        pts.push(ax);
        pts.push(ay);
        tex.push(-1);
        dst.push(dstValue);

        norms.push(abnx);
        norms.push(abny);
        norms.push(-abnx);
        norms.push(-abny);
      }
  }
}
